// 3. Internal components
import { Button } from '~/components/ui/button';
import { Spinner } from '~/components/ui/spinner';

/**
 * Props for WizardActions component.
 */
export interface WizardActionsProps {
  onBack?: () => void;
  onNext?: () => void;
  onSkip: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  showBack?: boolean;
  isLoading?: boolean;
}

/**
 * Wizard action buttons for navigation and skip functionality.
 *
 * Provides Back, Next, and Skip buttons with loading state support.
 * Skip button available on all steps per spec.
 *
 * Per §TS.3.1: Function declaration for named export.
 * Per §TS.3.2: Props interface with descriptive naming.
 *
 * @example
 * ```tsx
 * <WizardActions
 *   onBack={() => setStep(step - 1)}
 *   onNext={() => setStep(step + 1)}
 *   onSkip={handleSkip}
 *   nextLabel="Create Topic"
 *   nextDisabled={!topicName}
 *   showBack={true}
 *   isLoading={isCreating}
 * />
 * ```
 */
export function WizardActions({
  onBack,
  onNext,
  onSkip,
  nextLabel = 'Next',
  nextDisabled = false,
  showBack = true,
  isLoading = false,
}: WizardActionsProps) {
  return (
    <div className="mt-6 flex flex-wrap items-center justify-end gap-4">
      {/* Back Button - conditional visibility */}
      {showBack && onBack && (
        <Button type="button" variant="outline" onClick={onBack} disabled={isLoading}>
          Back
        </Button>
      )}

      {/* Next Button - primary action. Explicit type="button" so it never implicitly
          submits an enclosing <form>; steps that need Enter-to-submit call onNext
          from their own form's onSubmit handler instead, avoiding a double-fire. */}
      {onNext && (
        <Button
          type="button"
          variant="default"
          onClick={onNext}
          disabled={nextDisabled || isLoading}
          aria-busy={isLoading}
          className="min-w-[120px]"
        >
          {isLoading && (
            <Spinner
              data-icon="inline-start"
              data-testid="wizard-loading-spinner"
              aria-hidden="true"
            />
          )}
          {nextLabel}
        </Button>
      )}

      {/* Skip - de-emphasized */}
      <Button type="button" variant="link" onClick={onSkip} disabled={isLoading}>
        Skip onboarding
      </Button>
    </div>
  );
}
